import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';

import globalStyles from './pc-heading.lightDom.css.lit';

type HeadingVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h5'
  | 'h6'
  | 'eyebrow'
  | 'eyebrow-secondary';
type HeadingWeight = 'light' | 'normal' | 'bold' | 'medium' | 'normal';
type HeadingFamily = 'display' | 'body';
type HeadingColor = 'charcoal' | 'eggplant' | 'gray';
/**
 * Heading component
 * @element pc-heading
 * @slot - This element has a default slot
 */
@customElement('pc-heading')
export class PCHeading extends OutlineElement {
  adoptedStylesheets: AdoptedStyleSheets;

  @query('slot')
  defaultSlot!: HTMLSlotElement;

  @property()
  variant: HeadingVariant = 'h1';

  @property()
  weight: HeadingWeight;

  @property()
  family: HeadingFamily;

  @property()
  color: HeadingColor;

  connectedCallback() {
    super.connectedCallback();
    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);
  }

  getClasses() {
    return {
      [`heading-${this.variant}`]: this.variant,
      [`heading-${this.weight}`]: this.weight,
      [`heading-${this.family}`]: this.family,
      [`heading-${this.color}`]: this.color,
    };
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);

    const elementClasses = this.getClasses();
    const element = this.getTopLevelElementInSlot();

    if (element) {
      // Clear all classes
      element.className = '';

      Object.keys(elementClasses).forEach(key => {
        if (elementClasses[key]) {
          element.classList.add(key);
        }
      });
    }
  }

  render(): TemplateResult {
    return html` <slot></slot> `;
  }

  getTopLevelElementInSlot() {
    const assignedElements = this.defaultSlot?.assignedElements();
    return assignedElements?.[0];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-heading': PCHeading;
  }
}
